import {
  Checkbox,
  TextInput,
  Text,
  Textarea,
  NumberInput,
} from "@mantine/core";
import { DateInput, DatesProvider } from "@mantine/dates";
import { IconGps } from "@tabler/icons-react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import { useSelector } from "../../../../../../redux/hooks";
import { useMemo } from "react";
import {
  mantineDateOnChange,
  toMantineDateBound,
  toMantineDateString,
} from "src/utils/mantineDates";
import VaccineAttachment from "../sections/VaccineAttachment/VaccineAttachment";
import MedicationAcceptance from "./MedicationAcceptance";

interface Props {
  field: ControllerRenderProps<FieldValues, string>;
  unit: string;
  clearedProps: any;
  vaxid?: string | null;
  forwardDate?: boolean;
}

const InputCell = ({
  unit,
  field,
  clearedProps,
  vaxid,
  forwardDate = true,
}: Props) => {
  const { uppercase } = useSelector(state => state.preferencesSlice);
  const uppercaseStyles = useMemo(() => {
    const transform = {
      textTransform: uppercase ? "uppercase" : "none",
    };
    return {
      input: transform,
      item: {
        ...transform,
        wordBreak: "keep-all",
      },
      ...transform,
    };
  }, [uppercase]);
  const globalMaxDate = useMemo(
    () => (forwardDate ? undefined : new Date()),
    [forwardDate],
  );
  switch (unit) {
    case "text":
      return (
        <TextInput {...clearedProps} {...field} styles={uppercaseStyles} />
      );
    case "large/text":
      return (
        <Textarea
          {...clearedProps}
          {...field}
          styles={{
            ...uppercaseStyles,
          }}
          autosize
          minRows={1}
        />
      );
    case "num":
      return (
        <NumberInput
          {...clearedProps}
          {...field}
          styles={uppercaseStyles}
          hideControls
        />
      );
    case "check":
      return (
        <Checkbox icon={IconGps} size="lg" {...field} checked={field.value} />
      );
    case "date":
      return (
        <DateInput
          {...clearedProps}
          name={field.name}
          onBlur={field.onBlur}
          ref={field.ref}
          value={toMantineDateString(field.value)}
          onChange={mantineDateOnChange(field.onChange)}
          valueFormat="DD MMM YYYY"
          styles={uppercaseStyles}
          maxDate={toMantineDateBound(globalMaxDate)}
        />
      );
    case "day/month":
      const currentYear = new Date().getFullYear();
      const minDate = new Date(currentYear, 0, 1); // Jan 1
      const maxDate = new Date(currentYear, 11, 31); // Dec 31
      return (
        <DateInput
          {...clearedProps}
          name={field.name}
          onBlur={field.onBlur}
          ref={field.ref}
          value={toMantineDateString(field.value)}
          onChange={mantineDateOnChange(field.onChange)}
          valueFormat="DD MMM"
          minDate={toMantineDateBound(minDate)}
          maxDate={toMantineDateBound(maxDate)}
          styles={uppercaseStyles}
        />
      );
    case "attachment":
      return (
        <VaccineAttachment
          inputId={field.name}
          vaxid={vaxid || null}
          initialAttachments={field.value}
        />
      );
    case "sig/acceptance":
      return <MedicationAcceptance id={field.name} />;
    case "BLANK":
      return (
        <Text size="sm" ta="center" style={uppercaseStyles as any}>
          {field.value}
        </Text>
      );
    default:
      return <p>default</p>;
  }
};
export default InputCell;
